import type { Dispatch, SetStateAction } from 'react';
import type { RutgersCourse } from '../types/RutgersCourse';
import type { GroupedCourse, ScheduledCourse } from '../types/course';
import { normalizeBaseId } from '../lib/courseIdFormatter';

export interface CourseAddedDraft {
  before: readonly GroupedCourse[];
  after: readonly GroupedCourse[];
  added: GroupedCourse;
}
interface Dependencies {
  selectedCourses: GroupedCourse[];
  schedule: { term_id?: string } | null;
  activeSemester: string;
  SEMESTERS: readonly { id: string }[];
  getCourseDataByClassNumberMemoized: (id: string) => RutgersCourse[];
  mapRutgersCourseToScheduledCourse: (section: RutgersCourse) => ScheduledCourse;
  removalIntents: { invalidate: (id: string) => void };
  setSelectedCourses: Dispatch<SetStateAction<GroupedCourse[]>>;
  setHasUnsavedChanges: Dispatch<SetStateAction<boolean>>;
  markAsChanged: () => void;
  trackCourseAdded: () => void;
  scheduleDebug: (...args: unknown[]) => void;
  getParsedCourses: (term: string) => Promise<readonly { baseId: string }[]>;
  showToast: (message: string, kind: 'info' | 'error') => void;
  onAdded?: (addition: CourseAddedDraft) => void;
}
/** The page's existing Add consumer; mapping and catalog remain its dependencies. */
export function createCourseAdditionHandler(deps: Dependencies) {
  const { selectedCourses, schedule, activeSemester, SEMESTERS,
    getCourseDataByClassNumberMemoized, mapRutgersCourseToScheduledCourse,
    removalIntents, setSelectedCourses, setHasUnsavedChanges, markAsChanged,
    trackCourseAdded, scheduleDebug, getParsedCourses, showToast } = deps;
  return async (classId: string, sectionId: string = '') => {
    // if (!schedule || !coursesGrouped.has(classId)) return;

    if (classId.includes(':')) {
      const [baseId, sectionId] = classId.split(':');
      const allSections = getCourseDataByClassNumberMemoized(baseId);
      const section = allSections.find(c => c.id === sectionId);
      if (!section) {
        scheduleDebug('section not found');
        return;
      }

      const newGroupedCourse: GroupedCourse = {
        id: baseId,
        name: section?.courseName || '',
        sections: allSections.map(mapRutgersCourseToScheduledCourse),
        selectedSection: mapRutgersCourseToScheduledCourse(section!)
      };
      removalIntents.invalidate(normalizeBaseId(newGroupedCourse.id));
      const updatedSelectedCourses = [...selectedCourses, newGroupedCourse];
      setSelectedCourses(updatedSelectedCourses);
      setHasUnsavedChanges(true);
      markAsChanged(); // Mark that user made changes

      // Track course addition
      trackCourseAdded();
      deps.onAdded?.({ before: selectedCourses, after: updatedSelectedCourses, added: newGroupedCourse });
      return;
    }



    // classId from search is the baseId (e.g., "01:198:111")
    // Ensure we use a clean baseId for lookup (remove colons if present)
    const normalizedBaseId = classId.includes(':') && classId.split(':').length === 3
      ? normalizeBaseId(classId) // Format to 01:198:111
      : classId.replace(/\D/g, ''); // Format to 01198111

    // Try both formats if necessary, but prioritize the raw one for now since RutgersCourse objects use digits-only baseId
    let allSections = getCourseDataByClassNumberMemoized(normalizedBaseId);

    // Fallback to original classId if normalized didn't work and it's different
    if (allSections.length === 0 && normalizedBaseId !== classId) {
      allSections = getCourseDataByClassNumberMemoized(classId);
    }

    scheduleDebug('Lookup results for', classId, ':', allSections.length, 'sections found');

    // Prevent crash if no sections found
    if (allSections.length === 0) {
      console.warn("Could not find course data for baseId:", classId);

      // Try to find which semester it IS offered in
      const otherSemesters = SEMESTERS.map((semester) => semester.id);
      let offeredInSemester = '';
      const scheduleTerm = schedule?.term_id || activeSemester;

      for (const sem of otherSemesters) {
        if (sem === scheduleTerm) continue;
        try {
          // Note: getParsedCourses triggers a map rebuild, which is fine for a one-off error check
          const courses = await getParsedCourses(sem);
          if (courses.some(c => c.baseId === normalizedBaseId || c.baseId === classId)) {
            offeredInSemester = sem.split('-').map(s => s.charAt(0).toUpperCase() + s.slice(1)).join(' ');
            break;
          }
        } catch (e) {
          console.error(`Error checking semester ${sem}:`, e);
        }
      }

      const semesterDisplayName = activeSemester.split('-').map(s => s.charAt(0).toUpperCase() + s.slice(1)).join(' ');

      if (offeredInSemester) {
        showToast(
          `${classId} is not offered in ${semesterDisplayName}. It is offered in ${offeredInSemester}.`,
          'info'
        );
      } else {
        showToast(
          `${classId} is not in the ${semesterDisplayName} catalog. It may have been recently removed or redirected.`,
          'error'
        );
      }
      return;
    }

    // Prevent adding a course that's already in the list
    if (selectedCourses.some(c => c.id === classId)) {
      scheduleDebug("Course already in schedule:", classId);
      return;
    }

    // Add the new course to the local state
    const firstSection = allSections[0];
    const newGroupedCourse: GroupedCourse = {
      id: classId,
      name: firstSection.courseName || firstSection.fullTitle || 'Unknown Course',
      sections: allSections.map(mapRutgersCourseToScheduledCourse),
      selectedSection: mapRutgersCourseToScheduledCourse(firstSection)
    };

    removalIntents.invalidate(normalizeBaseId(newGroupedCourse.id));
    const updatedSelectedCourses = [...selectedCourses, newGroupedCourse];
    setSelectedCourses(updatedSelectedCourses);
    setHasUnsavedChanges(true);
    markAsChanged(); // Mark the normal course-add branch for configuration autosave.

    // Track course addition
    trackCourseAdded();
    deps.onAdded?.({ before: selectedCourses, after: updatedSelectedCourses, added: newGroupedCourse });
  };
}
